import { useTellescopeMutation } from "@tellescope/tanstack-query";
import { useCallback, useRef } from "react";

const FIELD_VIEW_THROTTLE_MS = 60_000;

export interface UseTrackFieldViewArgs {
  responseId: string | undefined;
  accessCode: string | undefined;
}

/**
 * Records per-field view beacons for the "Form Abandonment By View" analytics report, which groups
 * unsubmitted responses by the last field the enduser actually saw. Answer-based abandonment
 * misattributes that field on forms with skip logic, so views are tracked separately from answers.
 *
 * Call `trackFieldView` wherever a field becomes visible to the enduser. Repeat calls for the same
 * field within 60s are dropped, so callers do not need to deduplicate. Beacons are fire-and-forget:
 * the backend rejects a save against an already-submitted response, and a failed view is never worth
 * surfacing to the enduser.
 */
export const useTrackFieldView = ({ responseId, accessCode }: UseTrackFieldViewArgs) => {
  const saveFieldResponseMutation = useTellescopeMutation({
    model: "form_responses",
    method: "save_field_response",
  });

  const lastViewedAtByFieldId = useRef<Record<string, number>>({});

  const { mutate } = saveFieldResponseMutation;

  const trackFieldView = useCallback(
    (fieldId: string) => {
      if (!responseId && !accessCode) return;

      const now = Date.now();
      const lastViewedAt = lastViewedAtByFieldId.current[fieldId];
      if (lastViewedAt && now - lastViewedAt <= FIELD_VIEW_THROTTLE_MS) return;

      lastViewedAtByFieldId.current[fieldId] = now;

      mutate({ formResponseId: responseId, accessCode, viewOnly: true, fieldId });
    },
    [mutate, responseId, accessCode]
  );

  return trackFieldView;
};
